import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  ACCESS_REQUEST_RESOURCE_NAME_MAX,
  ACCESS_RESOURCE_TYPE_GROUPS,
  ACCESS_RESOURCE_TYPE_LABELS,
  ACCESS_RESOURCE_TYPE_PROVIDERS,
  type AccessResourceType,
} from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import { ACCESS_REVIEWERS, MENTIONABLE_USERS, type AccessReviewer, type MentionUser } from '../lib/access';
import { RichEditor } from '../components/RichEditor';
import { AttachProvider } from '../lib/files';
import { Icon, type IconName } from '../components/Icon';
import { AssigneesPicker, type AssigneeOption } from '../components/tasks/AssigneesPicker';
import { EntityPicker, INVENTORY_TYPES, type EntityPick } from '../components/requests/EntityPicker';
import { LabelPicker } from '../components/labels/LabelPicker';
import type { LabelLite } from '../components/labels/LabelChip';
import '../styles/requests-shared.css';
import '../styles/requests-new.css';
import '../styles/changes-new.css';
import '../styles/request-new.css';

/** A file needs a task id, which this form does not have yet: Attach shows disabled with this hint. */
const NEW_TASK_ATTACH = { taskId: null, hint: 'Attach after creating the task' };

/** Workflow labels are never picked here — a task changes column on the board. */
const NO_WORKFLOW_SCOPE = ['workflow'];

/**
 * /tasks/new — a plain issue form, like a GitLab issue; the conversation happens in comments.
 * Two templates (docs/tasks.md "Create a task"):
 *   - Task: title, description, assignees, labels, due date. Filed as template `blank`.
 *     ?template=change and ?template=blank (old links) open it too.
 *   - Access: which system, which one, why. Prefill: ?template=access&type=repo&entityId=42&name=group/proj.
 *     Assignees start as the default access assignees; they approve or deny it. "Access until" (empty = permanent).
 * A new member may only create Access, without labels (the API refuses them), and may only pick
 * assignees from the default access assignees. One createTask call carries everything.
 */
const CREATE = gql`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      id
    }
  }
`;

const DIRECTORY = gql`
  query TaskAssignableUsers {
    assignableUsers {
      email
      displayName
      role
    }
  }
`;

/** Access tasks: every staff person (any role) can be assigned; a new member may ask this too. */
const ACCESS_DIRECTORY = gql`
  query TaskAccessAssignableUsers {
    accessAssignableUsers {
      email
      displayName
      role
    }
  }
`;
type DirUser = { email: string; displayName?: string | null; role?: string | null };

type Template = 'task' | 'access';
const TEMPLATES: { value: Template; title: string; desc: string; icon: IconName }[] = [
  { value: 'task', title: 'Task', desc: 'Anything to do: a title, a description, people and a due date.', icon: 'docs' },
  { value: 'access', title: 'Access', desc: 'Access to a company system. The assignees approve it.', icon: 'access' },
];
/** ?template= → a card. `change` and `blank` are the old names of Task. */
const templateOf = (v: string | null): Template | null =>
  v === 'access' ? 'access' : v === 'task' || v === 'change' || v === 'blank' ? 'task' : null;

const TITLE_MAX = 160; // TASK_TITLE_MAX in inventory-core

/** Required fields carry a mark; optional ones say so. Every field gets exactly one. */
const ReqMark = () => (
  <span className="rq-req" title="Required">
    *
  </span>
);
const OptMark = ({ text = 'optional' }: { text?: string }) => <span className="req-label-opt">{text}</span>;

const DAY_MS = 86_400_000;
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const startOfLocalDay = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};
/** yyyy-mm-dd (a local calendar day) → that day's local end, so "due Oct 21" means all of Oct 21. */
const endOfLocalDay = (ymd: string) => new Date(startOfLocalDay(ymd).getTime() + DAY_MS - 1);

type FieldKey = 'template' | 'title' | 'reason' | 'name' | 'due' | 'labels' | 'assignees';
const FIELD_ID: Record<FieldKey, string> = {
  template: 'rq-f-template',
  title: 'rq-f-title',
  reason: 'rq-f-reason',
  name: 'rq-f-name',
  due: 'rq-f-due',
  labels: 'rq-f-labels',
  assignees: 'rq-f-assignees',
};

const sameSet = (a: string[], b: string[]) => {
  const x = new Set(a.map((e) => e.toLowerCase()));
  return x.size === b.length && b.every((e) => x.has(e.toLowerCase()));
};

/** The two template cards as one ARIA radio group (arrows move and select, wrapping). */
function TemplateCards({
  value,
  onChange,
  disabled = () => false,
  invalid,
}: {
  value: Template | null;
  onChange: (t: Template) => void;
  disabled?: (t: Template) => boolean;
  invalid?: boolean;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const enabled = TEMPLATES.map((t, i) => (disabled(t.value) ? -1 : i)).filter((i) => i >= 0);
  const checked = TEMPLATES.findIndex((t) => t.value === value);
  const tabStop = checked >= 0 ? checked : (enabled[0] ?? 0);
  const move = (from: number, delta: number) => {
    const pos = enabled.indexOf(from);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    if (next == null) return;
    onChange(TEMPLATES[next].value);
    refs.current[next]?.focus();
  };
  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      move(i, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      move(i, -1);
    }
  };
  return (
    <div
      className={`rq-tpl-cards${invalid ? ' rq-tpl-cards--invalid' : ''}`}
      role="radiogroup"
      aria-labelledby="rq-template-label"
      aria-required="true"
      aria-invalid={invalid || undefined}
    >
      {TEMPLATES.map((t, i) => {
        const on = t.value === value;
        const off = disabled(t.value);
        return (
          <button
            key={t.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={on}
            aria-disabled={off || undefined}
            disabled={off}
            tabIndex={i === tabStop ? 0 : -1}
            data-testid={`rq-template-${t.value}`}
            className={`rq-tpl-card${on ? ' rq-tpl-card--on' : ''}`}
            onClick={() => onChange(t.value)}
            onKeyDown={onKeyDown(i)}
          >
            <span className="rq-tpl-card__icon" aria-hidden="true">
              <Icon name={t.icon} size={16} />
            </span>
            <span className="rq-tpl-card__title">{t.title}</span>
            <span className="rq-tpl-card__desc">{t.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function TaskNewPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { me, loading: meLoading } = useMe();
  const isNewMember = !me || me.role === 'new_member';

  // ---- template ----
  const qTemplate = params.get('template');
  const prefillType = params.get('type');
  const prefillEntityId = params.get('entityId');
  const prefillName = params.get('name');
  // An access prefill (?type=…) without template= still means Access (old /access/new links).
  const initialTemplate: Template | null = templateOf(qTemplate) ?? (prefillType ? 'access' : null);
  const [chosen, setChosen] = useState<Template | null>(initialTemplate);
  // Every role may file both templates. A new member lands on Access (their usual ask) until they pick.
  const template: Template | null = chosen ?? (isNewMember && !meLoading ? 'access' : null);

  // ---- common ----
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [labels, setLabels] = useState<LabelLite[]>([]);
  // null = untouched: Access shows the default access assignees, Task starts empty.
  const [pickedAssignees, setPickedAssignees] = useState<string[] | null>(null);
  // Emails the user added or re-added themselves (not auto-added access defaults): they survive Access → Task.
  const userAdded = useRef<Set<string>>(new Set());
  const [due, setDue] = useState('');

  // ---- access ----
  const [resourceType, setResourceType] = useState<AccessResourceType>(
    (prefillType as AccessResourceType) || 'database',
  );
  const [resourceName, setResourceName] = useState(prefillName ?? '');
  // Only a system the inventory tracks can be linked; any other prefill is an editable typed name.
  const [picked, setPicked] = useState<EntityPick | null>(
    prefillEntityId && prefillType && prefillName && INVENTORY_TYPES.has(prefillType as AccessResourceType)
      ? { entityId: prefillEntityId, name: prefillName }
      : null,
  );
  const linked = !isNewMember && !!picked;
  const entity = linked ? picked : null;

  const [attempted, setAttempted] = useState(false);
  // Title is validated on blur too, not only on submit.
  const [titleBlurred, setTitleBlurred] = useState(false);
  const [mutErr, setMutErr] = useState<string | null>(null);
  const assigneeErr = mutErr && /assign/i.test(mutErr) ? mutErr : null;
  const errsumRef = useRef<HTMLDivElement>(null);

  const myEmail = (me?.email ?? '').toLowerCase();
  // Plain tasks: engineer+ only (a new member asks engineers for help). Access tasks: every staff person (accessAssignableUsers).
  const dir = useQuery<{ assignableUsers: DirUser[] }>(DIRECTORY, { skip: !me });
  const accessDir = useQuery<{ accessAssignableUsers: DirUser[] }>(ACCESS_DIRECTORY, { skip: !me });
  // Default access assignees (the API returns only the defaults). The requester is never one of their own.
  const { data: revData } = useQuery<{ accessReviewers: AccessReviewer[] }>(ACCESS_REVIEWERS);
  const defaults = useMemo(() => {
    const seen = new Set<string>();
    return (revData?.accessReviewers ?? []).filter((r) => {
      const e = r.email.toLowerCase();
      if (seen.has(e) || e === myEmail) return false;
      seen.add(e);
      return true;
    });
  }, [revData, myEmail]);
  const defaultEmails = useMemo(() => defaults.map((r) => r.email.toLowerCase()), [defaults]);
  const taskPeople: AssigneeOption[] = useMemo(() => {
    const out = new Map<string, AssigneeOption>();
    for (const u of dir.data?.assignableUsers ?? []) {
      if (u.role && u.role !== 'new_member')
        out.set(u.email.toLowerCase(), { email: u.email.toLowerCase(), label: u.displayName || u.email });
    }
    return [...out.values()];
  }, [dir.data]);
  const accessPeople: AssigneeOption[] = useMemo(() => {
    const out = new Map<string, AssigneeOption>();
    for (const r of defaults) {
      const e = r.email.toLowerCase();
      out.set(e, { email: e, label: r.displayName || r.email });
    }
    for (const u of accessDir.data?.accessAssignableUsers ?? []) {
      const e = u.email.toLowerCase();
      if (e !== myEmail && !out.has(e)) out.set(e, { email: e, label: u.displayName || u.email });
    }
    return [...out.values()];
  }, [accessDir.data, defaults, myEmail]);
  // Same source as the detail page's composers: @mentions work in the description and the access "Why".
  const { data: mentionData } = useQuery<{ mentionableUsers: MentionUser[] }>(MENTIONABLE_USERS, {
    skip: !me,
    fetchPolicy: 'cache-and-network',
  });
  const mentionUsers = mentionData?.mentionableUsers ?? [];

  const [create, { loading: creating }] = useMutation(CREATE);

  const isAccess = template === 'access';
  const assignees = pickedAssignees ?? (isAccess ? defaultEmails : []);
  const addAssignee = (email: string) => {
    const e = email.toLowerCase();
    userAdded.current.add(e);
    setPickedAssignees((cur) => {
      const base = cur ?? (isAccess ? defaultEmails : []);
      return base.includes(e) ? base : [...base, e];
    });
    if (assigneeErr) setMutErr(null);
  };
  const removeAssignee = (email: string) => {
    const e = email.toLowerCase();
    userAdded.current.delete(e);
    setPickedAssignees((cur) => (cur ?? (isAccess ? defaultEmails : [])).filter((x) => x !== e));
    if (assigneeErr) setMutErr(null);
  };

  const chooseTemplate = (t: Template) => {
    if (t === template) return;
    setChosen(t);
    setMutErr(null);
    // An untouched Assigned follows the template (Access → the defaults); an edited list is kept.
    // Access → Task drops the default access assignees the user did not add themselves.
    if (pickedAssignees) {
      if (template === 'access' && t === 'task') {
        const kept = pickedAssignees.filter((e) => !defaultEmails.includes(e) || userAdded.current.has(e));
        setPickedAssignees(kept.length ? kept : null);
      } else if (sameSet(pickedAssignees, defaultEmails) || !pickedAssignees.length) setPickedAssignees(null);
    }
    const next = new URLSearchParams(params);
    next.set('template', t);
    setParams(next, { replace: true });
  };

  const applyLabel = (l: LabelLite) =>
    // A task carries at most one label per scope: the new one replaces the old.
    setLabels((cur) => [...cur.filter((x) => x.id !== l.id && (!l.scope || x.scope !== l.scope)), l]);
  const removeLabel = (l: LabelLite) => setLabels((cur) => cur.filter((x) => x.id !== l.id));

  // ---- validation ----
  const titleTrim = title.trim();
  const nameTooLong = isAccess && !linked && resourceName.trim().length > ACCESS_REQUEST_RESOURCE_NAME_MAX;
  const dateLabel = isAccess ? 'Access until' : 'Due date';
  const errors: { field: FieldKey; msg: string }[] = [];
  if (!template) errors.push({ field: 'template', msg: 'Pick a template' });
  if (template && !isAccess && !titleTrim) errors.push({ field: 'title', msg: 'Title is required' });
  if (titleTrim.length > TITLE_MAX) errors.push({ field: 'title', msg: `Title must be at most ${TITLE_MAX} characters` });
  if (nameTooLong)
    errors.push({ field: 'name', msg: `Keep the target under ${ACCESS_REQUEST_RESOURCE_NAME_MAX} characters` });
  // The reason is the only place an assignee learns what is actually asked for.
  if (isAccess && !body.trim()) errors.push({ field: 'reason', msg: 'Say why you need it' });
  if (due && due < todayISO()) errors.push({ field: 'due', msg: `${dateLabel} can't be in the past` });
  if (labels.some((l) => l.name === 'workflow::done'))
    errors.push({ field: 'labels', msg: 'A new task cannot start in workflow::done' });
  const errOf = (f: FieldKey) =>
    attempted || (f === 'title' && titleBlurred) ? errors.find((e) => e.field === f)?.msg : undefined;

  const focusField = (f: FieldKey) => {
    const root = document.getElementById(FIELD_ID[f]);
    const target =
      f === 'template'
        ? root?.querySelector<HTMLElement>('[role="radio"][tabindex="0"]')
        : f === 'reason'
          ? root?.querySelector<HTMLElement>('[contenteditable="true"], textarea')
          : root?.querySelector<HTMLElement>('input, select, button');
    (target ?? root)?.focus();
  };

  /**
   * Assigned → createTask. Access: while every default chip is kept, send only the extra people and the
   * API adds the defaults itself; once a default chip is removed, send includeDefaultAssignees: false with
   * exactly the chips left. Task: the list as is.
   */
  const assigneeInput = (): { assigneeEmails: string[] | null; includeDefaultAssignees: boolean | null } => {
    if (!isAccess) return { assigneeEmails: assignees.length ? assignees : null, includeDefaultAssignees: null };
    if (pickedAssignees === null) return { assigneeEmails: null, includeDefaultAssignees: null };
    const set = new Set(assignees);
    if (defaultEmails.every((e) => set.has(e))) {
      const extras = assignees.filter((e) => !defaultEmails.includes(e));
      return { assigneeEmails: extras.length ? extras : null, includeDefaultAssignees: null };
    }
    return { assigneeEmails: assignees.length ? assignees : null, includeDefaultAssignees: false };
  };

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (creating) return;
    setAttempted(true);
    setMutErr(null);
    if (errors.length || !template) {
      requestAnimationFrame(() => errsumRef.current?.focus());
      return;
    }
    const input = {
      template: isAccess ? 'access' : 'blank',
      title: titleTrim || null,
      descriptionMd: body.trim() || null,
      // Access: the grant is valid until this day's end; empty = permanent.
      dueAt: due ? endOfLocalDay(due).toISOString() : null,
      ...assigneeInput(),
      labels: isNewMember ? [] : labels.map((l) => l.name),
      access: isAccess
        ? {
            resourceType,
            entityId: entity ? Number(entity.entityId) : null,
            resourceName: linked ? null : resourceName.trim() || null,
          }
        : null,
    };
    try {
      const res = await create({ variables: { input } });
      const id = (res.data as { createTask?: { id: number | string } } | null)?.createTask?.id;
      if (id != null) navigate(`/tasks/${id}`);
    } catch (err) {
      setMutErr(err instanceof Error ? err.message : 'Could not create the task');
    }
  };

  const inventory = !isNewMember && INVENTORY_TYPES.has(resourceType);
  const accessTitle = `Access: ${ACCESS_RESOURCE_TYPE_LABELS[resourceType]} "${
    entity?.name || resourceName.trim() || ACCESS_RESOURCE_TYPE_LABELS[resourceType]
  }"`.slice(0, TITLE_MAX);

  const header = (
    <>
      <div className="detail-back">
        <Link to="/tasks" className="muted">
          ← Tasks
        </Link>
      </div>
      <div className="detail-title rq-new__head">
        <div className="detail-title__row">
          <h2 className="detail-title__name">New task</h2>
        </div>
        <p className="detail-title__facts">
          Fields marked <span className="rq-req">*</span> are required. Discuss it in the comments once it is
          created.
        </p>
      </div>
    </>
  );

  if (meLoading) {
    return (
      <div className="page page--wide cr-new rq-new" data-testid="rq-new">
        {header}
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const titleErr = errOf('title');
  const templateErr = errOf('template');
  const dueErr = errOf('due');
  const reasonErr = errOf('reason');
  const labelsErr = errOf('labels');
  const titleLen = titleTrim.length;
  // The one line about who approves an access task.
  const assigneesHint = isAccess
    ? assignees.length
      ? 'The assignees approve or deny it.'
      : 'Nobody assigned: only a root or maintainer can approve it.'
    : '';

  const assignedField = (
    <div className="access-field" id={FIELD_ID.assignees}>
      <label htmlFor="rq-assignees-input">
        <strong>Assignees</strong> <OptMark />
      </label>
      <div data-testid="rq-assignees">
        <AssigneesPicker
          id="rq-assignees-input"
          ariaLabel="Assignees"
          value={assignees}
          options={isAccess ? accessPeople : taskPeople}
          onAdd={addAssignee}
          onRemove={removeAssignee}
          disabled={creating}
        />
      </div>
      {assigneesHint && <p className="req-field-hint">{assigneesHint}</p>}
      {assigneeErr && (
        <p className="error text-sm rq-inline-err" role="alert" data-testid="rq-assignee-error">
          {assigneeErr}
        </p>
      )}
    </div>
  );

  const dateField = (
    <div className={`access-field${dueErr ? ' cr-invalid' : ''}`} id={FIELD_ID.due}>
      <label htmlFor="rq-due-input">
        <strong>{dateLabel}</strong> <OptMark />
      </label>
      <input
        id="rq-due-input"
        data-testid="rq-due"
        type="date"
        className="rq-date"
        value={due}
        min={todayISO()}
        aria-invalid={!!dueErr || undefined}
        aria-describedby={[isAccess ? 'rq-due-hint' : '', dueErr ? 'rq-due-err' : ''].filter(Boolean).join(' ') || undefined}
        onChange={(e) => setDue(e.target.value)}
      />
      {isAccess && (
        <p className="req-field-hint" id="rq-due-hint">
          Leave empty for permanent access
        </p>
      )}
      {dueErr && (
        <p className="cr-field-err" id="rq-due-err">
          <Icon name="alert" size={13} /> {dueErr}.
        </p>
      )}
    </div>
  );

  return (
    <div className="page page--wide cr-new rq-new" data-testid="rq-new">
      {header}

      <div className="rq-layout">
        <form className="stack rq-form" noValidate onSubmit={(e) => void submit(e)}>
          <div className="rq-form-body">
            {attempted && errors.length > 0 && (
              <div className="cr-errsum" role="alert" tabIndex={-1} ref={errsumRef} data-testid="rq-errsum">
                <b>
                  {errors.length === 1
                    ? '1 thing to fix before this can be created'
                    : `${errors.length} things to fix before this can be created`}
                </b>
                <ul>
                  {errors.map((er) => (
                    <li key={er.field}>
                      <a
                        href={`#${FIELD_ID[er.field]}`}
                        onClick={(ev) => {
                          ev.preventDefault();
                          focusField(er.field);
                        }}
                      >
                        {er.msg}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <section className="rq-sec" aria-labelledby="rq-template-label">
              <h3 className="section" id="rq-template-label">
                Template <ReqMark />
              </h3>
              <div className="access-field" id={FIELD_ID.template}>
                <TemplateCards
                  value={template}
                  onChange={chooseTemplate}
                  invalid={!!templateErr}
                />
                {templateErr && (
                  <p className="cr-field-err">
                    <Icon name="alert" size={13} /> {templateErr}.
                  </p>
                )}
              </div>
            </section>

            {template && (
              <>
                {isAccess && (
                  <section className="rq-sec" aria-labelledby="rq-access-h" data-testid="rq-access">
                    <h3 className="section" id="rq-access-h">
                      Access
                    </h3>
                    <div className="access-field">
                      <label htmlFor="rq-system">
                        <strong>Which system?</strong> <ReqMark />
                      </label>
                      <select
                        id="rq-system"
                        data-testid="rq-system"
                        className="class-select"
                        value={resourceType}
                        disabled={linked}
                        aria-required="true"
                        onChange={(e) => {
                          setResourceType(e.target.value as AccessResourceType);
                          setPicked(null); // an entity belongs to one system's inventory
                        }}
                      >
                        {ACCESS_RESOURCE_TYPE_GROUPS.map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.types.map((t) => (
                              <option key={t} value={t}>
                                {ACCESS_RESOURCE_TYPE_LABELS[t]}
                                {ACCESS_RESOURCE_TYPE_PROVIDERS[t] ? ` — ${ACCESS_RESOURCE_TYPE_PROVIDERS[t]}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <div className={`access-field${nameTooLong ? ' cr-invalid' : ''}`} id={FIELD_ID.name}>
                      <label htmlFor="rq-name">
                        <strong>Which one?</strong> <OptMark />
                      </label>
                      <EntityPicker
                        id="rq-name"
                        resourceType={resourceType}
                        inventory={inventory}
                        name={resourceName}
                        onNameChange={setResourceName}
                        picked={entity}
                        onPick={(p) => {
                          setPicked(p);
                          if (p) setResourceName(p.name);
                        }}
                        invalid={nameTooLong}
                        placeholder={
                          inventory
                            ? `Search ${ACCESS_RESOURCE_TYPE_LABELS[resourceType].toLowerCase()}s, or type a name`
                            : 'e.g. signagecloud-qa, grafana-v2.opstech.org'
                        }
                      />
                      {linked ? (
                        <p className="req-field-hint">Linked to the inventory, so the assignees see the exact resource.</p>
                      ) : (
                        <p className="req-field-hint">
                          {inventory ? 'Search the inventory — or type a name if it isn’t listed. ' : ''}
                          Leave blank if the reason below already says it. Defaults to &ldquo;
                          {ACCESS_RESOURCE_TYPE_LABELS[resourceType]}&rdquo;.
                        </p>
                      )}
                      {nameTooLong && (
                        <p className="req-field-err error">
                          <Icon name="cross" size={13} className="req-ico" /> Keep it under{' '}
                          {ACCESS_REQUEST_RESOURCE_NAME_MAX} characters.
                        </p>
                      )}
                    </div>
                  </section>
                )}

                <section className="rq-sec" aria-labelledby="rq-details-h">
                  <h3 className="section" id="rq-details-h">
                    Details
                  </h3>
                  <div className={`access-field${titleErr ? ' cr-invalid' : ''}`} id={FIELD_ID.title}>
                    <div className="cr-title-row">
                      <label htmlFor="rq-title-input">
                        <strong>Title</strong> {isAccess ? <OptMark /> : <ReqMark />}
                      </label>
                      <span
                        className={`cr-counter${titleLen > TITLE_MAX ? ' cr-counter--over' : ''}`}
                        id="rq-title-count"
                        aria-live="polite"
                      >
                        {titleLen}/{TITLE_MAX}
                      </span>
                    </div>
                    <input
                      id="rq-title-input"
                      data-testid="rq-title"
                      className="rq-input"
                      value={title}
                      aria-required={!isAccess}
                      aria-invalid={!!titleErr || undefined}
                      aria-describedby={`rq-title-count${titleErr ? ' rq-title-err' : ''}`}
                      placeholder={
isAccess ? accessTitle : 'What needs to be done?'
                      }
                      onChange={(e) => setTitle(e.target.value)}
                      onBlur={() => setTitleBlurred(true)}
                    />
                    {isAccess && !titleTrim && (
                      <p className="req-field-hint">Left blank, the task is titled {accessTitle}.</p>
                    )}
                    {titleErr && (
                      <p className="cr-field-err" id="rq-title-err">
                        <Icon name="alert" size={13} /> {titleErr}.
                      </p>
                    )}
                  </div>

                  <div className={`access-field${reasonErr ? ' cr-invalid' : ''}`} id={FIELD_ID.reason}>
                    <label>
                      <strong>{isAccess ? 'Why do you need it?' : 'Description'}</strong>{' '}
                      {isAccess ? <ReqMark /> : <OptMark text="optional · markdown" />}
                    </label>
                    <div data-testid="rq-body">
                      {/* Keyed by template: the editor reads its placeholder on mount only. */}
                      <AttachProvider value={NEW_TASK_ATTACH}>
                      <RichEditor
                        key={template}
                        value={body}
                        onChange={setBody}
                        disabled={creating}
                        onSubmit={() => void submit()}
                        mentionUsers={mentionUsers}
                        placeholder={
                          isAccess
                            ? 'What are you trying to do, and which exact resource? Specific reasons are approved faster.'
                            : 'Describe the task. @mention people to notify them.'
                        }
                      />
                      </AttachProvider>
                    </div>
                    {reasonErr && (
                      <p className="cr-field-err">
                        <Icon name="alert" size={13} /> {reasonErr}.
                      </p>
                    )}
                  </div>
                </section>

                <section className="rq-sec" aria-labelledby="rq-tracking-h">
                  <h3 className="section" id="rq-tracking-h">
                    Tracking
                  </h3>
                  {/* Assignees | Labels, then the date. A new member gets Assignees | Access until. */}
                  <div className="rq-meta-grid">
                    {assignedField}

                    {!isNewMember && (
                      <div className={`access-field${labelsErr ? ' cr-invalid' : ''}`} id={FIELD_ID.labels}>
                        <label>
                          <strong>Labels</strong> <OptMark />
                        </label>
                        {/* The picker renders the applied chips (removable) itself. */}
                        <div className="rq-labels" data-testid="rq-labels">
                          <LabelPicker
                            value={labels}
                            onApply={applyLabel}
                            onRemove={removeLabel}
                            disabled={creating}
                            excludeScopes={NO_WORKFLOW_SCOPE}
                          />
                        </div>
                        {labelsErr && (
                          <p className="cr-field-err">
                            <Icon name="alert" size={13} /> {labelsErr}.
                          </p>
                        )}
                      </div>
                    )}

                    {dateField}
                  </div>
                </section>
              </>
            )}

            {mutErr && !assigneeErr && (
              <p className="error" role="alert" data-testid="rq-error">
                {mutErr}
              </p>
            )}
          </div>

          <div className="btn-row rq-actions">
            <button type="submit" className="btn btn-primary" data-testid="rq-submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create task'}
            </button>
            <Link to="/tasks" className="btn btn-ghost">
              Cancel
            </Link>
          </div>
        </form>

      </div>
    </div>
  );
}
